import React, { useState } from "react";
import {
  Calendar,
  CreditCard,
  MessageSquare,
  BarChart,
  UserPlus,
  PenTool,
  FileText,
  Smartphone,
  Expand,
  X,
  Search,
  Zap,
  Users,
  ShoppingBag,
  Brain,
  Clock,
  Shield,
  Sparkles,
  TrendingUp,
  Globe,
  Star,
  CheckCircle,
  Music,
  Music2,
  Dumbbell,
  Waves,
  Tent,
  Video,
  ListTodo,
  TrendingUp as TrendingUpIcon,
  Edit3,
  UserCheck
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import GlobalSearchDemo from "@/components/home/GlobalSearchDemo";

const FeatureSection = () => {
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  
  // Core game-changing features that differentiate Hivetivity
  const heroFeatures = [
    {
      id: "hiveshare",
      title: "HiveShare - Safe Video Sharing for Kids",
      description: "Stop worrying about YouTube ads and privacy risks. Share class videos, recitals, and progress updates through our secure, kid-safe platform. Parents love it, you control it completely.",
      icon: Video,
      color: "bg-gradient-to-br from-red-50 to-pink-50",
      iconColor: "text-red-600",
      image: "https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/9b03204d-37fe-4cf2-1e82-3ce5752ba400/public",
      badge: "Protect Kids Online",
      badgeColor: "bg-gradient-to-r from-red-600 to-pink-600"
    },
    {
      id: "parent-automation",
      title: "Get 15+ Hours Back Every Week",
      description: "Your parent portal handles the repetitive stuff automatically. Makeup requests, payment reminders, schedule updates - all done without you lifting a finger. It's like having a full-time assistant.",
      icon: Zap,
      color: "bg-gradient-to-br from-orange-50 to-red-50",
      iconColor: "text-orange-600",
      image: "https://media.hivetivity.com/Picyard_1750195595445.svg",
      reverse: true,
      badge: "Your Time is Precious",
      badgeColor: "bg-gradient-to-r from-orange-600 to-red-600"
    },
    {
      id: "ai-intelligence",
      title: "Ask Your Business Anything, Get Real Answers",
      description: "No more digging through reports or guessing about your numbers. Just ask: 'Which classes make the most money?' or 'Who hasn't paid this month?' Your AI assistant knows everything about your business.",
      icon: Brain,
      color: "bg-gradient-to-br from-green-50 to-emerald-50",
      iconColor: "text-green-600",
      image: "https://media.hivetivity.com/Picyard_1750196614491.svg",
      badge: "Instant Business Insights",
      badgeColor: "bg-gradient-to-r from-green-600 to-emerald-600"
    },
    {
      id: "comprehensive-platform",
      title: "Stop Juggling 5+ Different Tools",
      description: "Scheduling, billing, parent communication, staff management, e-commerce - everything works together seamlessly. One login, one system, zero headaches. Finally, software that makes sense.",
      icon: BarChart,
      color: "bg-gradient-to-br from-purple-50 to-pink-50",
      iconColor: "text-purple-600",
      image: "https://media.hivetivity.com/Picyard_1750132926868.svg",
      reverse: true,
      badge: "Simple & Complete",
      badgeColor: "bg-gradient-to-r from-purple-600 to-pink-600"
    },
    {
      id: "bill-assist",
      title: "Never Chase Payments Again",
      description: "Bill Assist watches your money like a hawk. It knows which families pay late, which payments to prioritize, and exactly what to say to get paid faster. Your revenue just got smarter.",
      icon: TrendingUpIcon,
      color: "bg-gradient-to-br from-yellow-50 to-amber-50",
      iconColor: "text-yellow-600",
      image: "https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/db4e5dce-cf8d-4ca7-4e16-3aaf34360800/public",
      badge: "Get Paid Faster",
      badgeColor: "bg-gradient-to-r from-yellow-600 to-amber-600"
    },
    {
      id: "esignature-forms",
      title: "Paperwork That Actually Gets Done",
      description: "Create forms, collect signatures, store everything safely - all built-in. No more chasing parents for waivers or losing important documents. Digital paperwork that just works.",
      icon: Edit3,
      color: "bg-gradient-to-br from-teal-50 to-cyan-50",
      iconColor: "text-teal-600",
      image: "https://media.hivetivity.com/Picyard_1750196614491.svg",
      reverse: true,
      badge: "Go Paperless",
      badgeColor: "bg-gradient-to-r from-teal-600 to-cyan-600"
    }
  ];

  // Latest innovations and enhancements
  const latestInnovations = [
    {
      id: "global-search",
      title: "Global Search",
      description: "Find any student, class, or family instantly. No more hunting through menus or spreadsheets.",
      icon: Search,
      color: "bg-gradient-to-br from-blue-50 to-indigo-50",
      iconColor: "text-blue-600",
      hasDemo: true
    },
    {
      id: "hivepad",
      title: "HivePad Task Manager",
      description: "Your personal productivity assistant. Track family tasks, set priorities, never forget a follow-up.",
      icon: ListTodo,
      color: "bg-gradient-to-br from-indigo-50 to-purple-50", 
      iconColor: "text-indigo-600"
    },
    {
      id: "checkin-kiosk",
      title: "Check-In Kiosk",
      description: "Students love checking themselves in with our animated celebration system. Makes arrivals fun and accurate.",
      icon: UserCheck,
      color: "bg-gradient-to-br from-emerald-50 to-green-50",
      iconColor: "text-emerald-600"
    }
  ];

  // Comprehensive feature categories
  const featureCategories = [
    {
      title: "Student & Family Management",
      description: "Family-focused approach that makes sense",
      icon: Users,
      color: "bg-blue-50",
      features: [
        "Family-based account structure with sibling management",
        "Digital registration forms that auto-populate your system",
        "Emergency contacts and medical information tracking",
        "Student progress and attendance monitoring",
        "Parent portal access with personalized dashboards",
        "Waitlist management with automatic notifications"
      ]
    },
    {
      title: "Smart Scheduling & Classes",
      description: "Drag-and-drop simplicity with powerful automation",
      icon: Calendar,
      color: "bg-green-50",
      features: [
        "Conflict-free scheduling with automatic double-booking prevention",
        "Multi-location and multi-room management",
        "Instructor assignments with substitute management",
        "Recurring class templates and seasonal scheduling",
        "Capacity management with enrollment tracking",
        "Makeup class policies with parent self-scheduling"
      ]
    },
    {
      title: "Automated Billing & Payments",
      description: "Set it and forget it financial management",
      icon: CreditCard,
      color: "bg-purple-50",
      features: [
        "Automated monthly billing with family discounts",
        "Stripe integration for secure payment processing",
        "Online payment portal for parents",
        "Invoice tracking and payment reminders",
        "Financial reporting and revenue forecasting",
        "Registration fees and tuition prepayment options"
      ]
    },
    {
      title: "Parent Communication Hub",
      description: "Reduce emails by 70% with smart automation",
      icon: MessageSquare,
      color: "bg-orange-50",
      features: [
        "Automated class reminders and schedule changes",
        "Direct messaging between parents and staff",
        "Bulk announcements to specific classes or all families",
        "Document sharing and policy distribution",
        "Push notifications and SMS integration",
        "Email templates and automated workflows"
      ]
    },
    {
      title: "Business Intelligence & Reporting",
      description: "Data-driven insights without the complexity",
      icon: TrendingUp,
      color: "bg-pink-50",
      features: [
        "Real-time dashboards with key performance metrics",
        "Student retention and enrollment analytics",
        "Revenue tracking and financial forecasting",
        "Attendance patterns and class performance",
        "Custom report builder with export options",
        "AI-powered business recommendations"
      ]
    },
    {
      title: "Built-in E-Commerce",
      description: "Sell merchandise without extra tools",
      icon: ShoppingBag,
      color: "bg-indigo-50",
      features: [
        "Integrated apparel and merchandise shop",
        "Inventory management with size variations",
        "Online ordering linked to family accounts",
        "Secure checkout and payment processing",
        "Order tracking and fulfillment management",
        "Revenue integration with main billing system"
      ]
    }
  ];

  // Industry-specific showcase
  const industryFeatures = [
    {
      title: "Dance Studios",
      description: "Recital management, costume tracking, competition prep",
      icon: Music,
      color: "bg-pink-100"
    },
    {
      title: "Gymnastics & Kids Gyms", 
      description: "Equipment scheduling, skill progression, meet registration",
      icon: Dumbbell,
      color: "bg-blue-100"
    },
    {
      title: "Swim Schools",
      description: "Pool scheduling, certification tracking, safety compliance",
      icon: Waves,
      color: "bg-cyan-100"
    },
    {
      title: "Summer Camps",
      description: "Session enrollment, activity scheduling, pickup management",
      icon: Tent,
      color: "bg-green-100"
    },
    {
      title: "Music Schools",
      description: "Instrument rentals, recital prep, ensemble coordination",
      icon: Music2,
      color: "bg-purple-100"
    },
    {
      title: "Martial Arts",
      description: "Belt progression, tournament management, testing schedules",
      icon: Shield,
      color: "bg-red-100"
    }
  ];
  
  return (
    <>
      {/* Hero Features - Game Changers */}
      <section className="py-12 md:py-16 relative overflow-hidden bg-white">
        {/* Subtle background - matching homepage */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#FA8C00] blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-[#4ba3c7] blur-3xl"></div>
        </div>
        
        <div className="w-full px-3 sm:px-4 lg:px-6 xl:px-8 relative z-10">
          <div className="space-y-24">
            {heroFeatures.map((feature, index) => (
              <div 
                key={feature.id}
                className={`flex flex-col ${feature.reverse ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 md:gap-16 items-center`}
              >
                <div className="flex-1">
                  {feature.badge && (
                    <div className="inline-block bg-primary text-white text-sm font-semibold px-4 py-2 rounded-full mb-4">
                      {feature.badge}
                    </div>
                  )}
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
                
                <div className="flex-1 w-full">
                  {feature.hasDemo ? (
                    <GlobalSearchDemo />
                  ) : (
                    <div className={`${feature.id === 'parent-automation' ? 'max-w-xs mx-auto' : ''}`}>
                      <div 
                        className="relative group cursor-pointer"
                        onClick={() => setExpandedImage(feature.image)}
                      >
                        <img 
                          src={feature.image} 
                          alt={feature.title} 
                          className="w-full h-auto max-h-96 object-contain rounded-lg shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 rounded-lg flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                            <Expand className="h-6 w-6 text-gray-700" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* CTA Button after hero features */}
          <div className="text-center mt-16">
            <button 
              onClick={() => window.location.href = '/business-trial'}
              className="bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Start Your Free Trial
            </button>
            <p className="text-secondary/60 mt-3 text-sm">
              No credit card required • Setup in minutes
            </p>
          </div>
        </div>
      </section>

      {/* Latest Innovations */}
      <section className="py-16 bg-white relative overflow-hidden">
        {/* Subtle background */}
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#4ba3c7] blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-[#FA8C00] blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-block bg-gradient-to-r from-[#FA8C00] to-[#4ba3c7] text-white text-sm font-semibold px-6 py-2 rounded-full mb-6">
              Latest Innovations
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-3 md:mb-4">
              We Never Stop <span className="text-[#FA8C00]">Improving</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Your feedback drives our innovation. Here are some of the latest features we've added to make your life even easier.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {latestInnovations.map((innovation, index) => (
              <div 
                key={innovation.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer border border-gray-100"
              >
                <div className={`${innovation.color} p-6 relative overflow-hidden`}>
                  {/* Animated background overlay */}
                  <div className="absolute inset-0 bg-white/10 translate-x-full group-hover:translate-x-0 transition-transform duration-700"></div>
                  
                  <div className="h-16 w-16 flex items-center justify-center bg-white/20 rounded-xl backdrop-blur-sm mx-auto mb-4 relative z-10 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:bg-white/30">
                    <innovation.icon className="h-8 w-8 text-gray-700" />
                  </div>
                </div>
                
                <div className="p-6 bg-white group-hover:bg-gray-50 transition-colors duration-300">
                  <h3 className="text-xl font-bold mb-3 text-gray-800 transition-colors duration-300 group-hover:text-gray-900">
                    {innovation.title}
                  </h3>
                  <p className="text-gray-600 transition-colors duration-300 group-hover:text-gray-700 leading-relaxed">
                    {innovation.description}
                  </p>
                </div>
                
                {/* Subtle border animation */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-white/20 transition-colors duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comprehensive Feature Categories */}
      <section className="py-16 bg-gray-50/30 relative overflow-hidden">
        {/* Subtle background - matching homepage */}
        <div className="absolute inset-0 z-0 opacity-15">
          <div className="absolute top-20 right-20 w-72 h-72 rounded-full bg-[#4ba3c7] blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#FA8C00] blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-3 md:mb-4">
              Every Feature Your Business <span className="text-[#FA8C00]">Actually Needs</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Comprehensive functionality designed specifically for youth program owners. No more piecing together different tools.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 mb-12">
            {featureCategories.map((category, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer border border-gray-100"
              >
                <div className={`${category.color} p-6 relative overflow-hidden`}>
                  {/* Animated background overlay */}
                  <div className="absolute inset-0 bg-white/10 translate-x-full group-hover:translate-x-0 transition-transform duration-700"></div>
                  
                  <div className="flex items-center relative z-10">
                    <div className="h-16 w-16 flex items-center justify-center bg-white/20 rounded-xl backdrop-blur-sm mr-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:bg-white/30">
                      <category.icon className="h-8 w-8 text-gray-700" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-1">
                        {category.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {category.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 bg-white group-hover:bg-gray-50 transition-colors duration-300">
                  <ul className="space-y-3">
                    {category.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start">
                        <div className="bg-[#FA8C00]/20 p-1 rounded-full mr-3 mt-0.5 flex-shrink-0">
                          <div className="w-2 h-2 bg-[#FA8C00] rounded-full"></div>
                        </div>
                        <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Subtle border animation */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-white/20 transition-colors duration-300"></div>
              </div>
            ))}
          </div>

          {/* CTA Button after comprehensive features */}
          <div className="text-center">
            <button 
              onClick={() => window.location.href = '/business-trial'}
              className="bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Start Your Free Trial
            </button>
            <p className="text-gray-600 mt-3 text-sm">
              No credit card required • Setup in minutes
            </p>
          </div>
        </div>
      </section>

      {/* Industry-Specific Features */}
      <section className="py-16 bg-white relative overflow-hidden">
        {/* Subtle background - matching homepage */}
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#FA8C00] blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-[#4ba3c7] blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-3 md:mb-4">
              Built for <span className="text-[#FA8C00]">Your Industry</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
               to your specific type of youth program with industry-tailored features and terminology.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industryFeatures.map((industry, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer border border-gray-100"
              >
                <div className={`${industry.color} p-6 relative overflow-hidden`}>
                  {/* Animated background overlay */}
                  <div className="absolute inset-0 bg-white/10 translate-x-full group-hover:translate-x-0 transition-transform duration-700"></div>
                  
                  <div className="flex items-center relative z-10">
                    <div className="h-12 w-12 flex items-center justify-center bg-white/20 rounded-xl backdrop-blur-sm mr-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:bg-white/30">
                      <industry.icon className="h-6 w-6 text-gray-700" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {industry.title}
                    </h3>
                  </div>
                </div>
                
                <div className="p-6 bg-white group-hover:bg-gray-50 transition-colors duration-300">
                  <p className="text-gray-600 leading-relaxed">
                    {industry.description}
                  </p>
                </div>
                
                {/* Subtle border animation */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-white/20 transition-colors duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon: Hive360 */}
      <section className="py-16 bg-gradient-to-br from-[#FA8C00]/5 to-[#4ba3c7]/5 relative overflow-hidden">
        {/* Subtle background - matching homepage */}
        <div className="absolute inset-0 z-0 opacity-15">
          <div className="absolute top-20 right-20 w-72 h-72 rounded-full bg-[#4ba3c7] blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#FA8C00] blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block bg-green-600 text-white text-sm font-semibold px-6 py-2 rounded-full mb-6">
              Now Available
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              Introducing <span className="text-[#FA8C00]">Hive360</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8">
              Seamlessly embed your Hivetivity class schedule directly on your website. Parents can browse and enroll in classes without leaving your site, while you maintain complete control over branding, enrollment policies, and the customer experience. Zero dependencies, instant setup.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer border border-gray-100">
                <div className="bg-gradient-to-br from-[#FA8C00] to-[#FBA94C] p-6 relative overflow-hidden">
                  {/* Animated background overlay */}
                  <div className="absolute inset-0 bg-white/10 translate-x-full group-hover:translate-x-0 transition-transform duration-700"></div>
                  
                  <div className="h-12 w-12 flex items-center justify-center bg-white/20 rounded-xl backdrop-blur-sm mx-auto mb-4 relative z-10 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:bg-white/30">
                    <Globe className="h-6 w-6 text-gray-700" />
                  </div>
                </div>
                <div className="p-6 bg-white group-hover:bg-gray-50 transition-colors duration-300">
                  <h3 className="font-bold text-gray-800 mb-2">Embeddable Widget</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Seamlessly integrate class discovery into your existing website</p>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer border border-gray-100">
                <div className="bg-gradient-to-br from-[#4ba3c7] to-[#7dbdd9] p-6 relative overflow-hidden">
                  {/* Animated background overlay */}
                  <div className="absolute inset-0 bg-white/10 translate-x-full group-hover:translate-x-0 transition-transform duration-700"></div>
                  
                  <div className="h-12 w-12 flex items-center justify-center bg-white/20 rounded-xl backdrop-blur-sm mx-auto mb-4 relative z-10 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:bg-white/30">
                    <Sparkles className="h-6 w-6 text-gray-700" />
                  </div>
                </div>
                <div className="p-6 bg-white group-hover:bg-gray-50 transition-colors duration-300">
                  <h3 className="font-bold text-gray-800 mb-2">Custom Branding</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Match your website's look and feel perfectly</p>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer border border-gray-100">
                <div className="bg-gradient-to-br from-[#7ad4a0] to-[#a5e4c0] p-6 relative overflow-hidden">
                  {/* Animated background overlay */}
                  <div className="absolute inset-0 bg-white/10 translate-x-full group-hover:translate-x-0 transition-transform duration-700"></div>
                  
                  <div className="h-12 w-12 flex items-center justify-center bg-white/20 rounded-xl backdrop-blur-sm mx-auto mb-4 relative z-10 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:bg-white/30">
                    <TrendingUp className="h-6 w-6 text-gray-700" />
                  </div>
                </div>
                <div className="p-6 bg-white group-hover:bg-gray-50 transition-colors duration-300">
                  <h3 className="font-bold text-gray-800 mb-2">Analytics Dashboard</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Track widget performance and enrollment conversions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Image Lightbox Modal */}
      <AnimatePresence>
        {expandedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
            onClick={() => setExpandedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 30 }}
              transition={{ type: "spring", duration: 0.6, bounce: 0.3 }}
              className="relative max-w-[95vw] max-h-[95vh] bg-white rounded-3xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Feature Preview</h3>
                  <button
                    onClick={() => setExpandedImage(null)}
                    className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110"
                  >
                    <X className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <div className={`${expandedImage === 'https://media.hivetivity.com/Picyard_1750195595445.svg' ? 'max-w-md mx-auto' : ''}`}>
                  <img
                    src={expandedImage}
                    alt="Feature - Expanded View"
                    className="w-full h-full object-contain rounded-2xl"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FeatureSection;
